import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Banknote, CreditCard, Receipt, X } from "lucide-react";

import { useTranslation } from "react-i18next";
/**
 * Floating quick-entry FAB for CSAs to record real-time online / credit / expense
 * entries against their active shift's draft — without leaving the current page.
 * Only shows for CSA on protected app pages.
 */
export default function QuickEntryFAB() {
  const { t: __t } = useTranslation();
  const { user } = useAuth();
  const loc = useLocation();
  const [attendance, setAttendance] = useState(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(null); // 'online' | 'credit' | 'expense'

  const hidden =
    !user ||
    user.role !== "CSA" ||
    ["/login", "/register", "/customer"].some((p) => loc.pathname.startsWith(p));

  // Poll active check-in — FAB only appears when the CSA is actually in a shift
  useEffect(() => {
    if (hidden) { setAttendance(null); return; }
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/attendance/active");
        if (!cancelled) setAttendance(data.attendance || null);
      } catch { if (!cancelled) setAttendance(null); }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [hidden, loc.pathname]);

  if (hidden || !attendance) return null;

  return (
    <>
      {/* FAB */}
      <button
        type="button"
        onClick={() => { setTab(null); setOpen(true); }}
        className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-amber-500 text-white shadow-2xl transition hover:scale-105 hover:bg-amber-600 focus:outline-none focus:ring-4 focus:ring-amber-300 md:bottom-8 md:right-8"
        data-testid="quick-entry-fab"
        aria-label={__t("components.quickentryfab.quick_entry")}
      >
        <Plus className="h-6 w-6" />
      </button>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="sm:max-w-md" data-testid="quick-entry-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{__t("components.quickentryfab.quick_entry")}</span>
              <button onClick={() => setOpen(false)} className="rounded-md p-1 hover:bg-secondary" data-testid="quick-close">
                <X className="h-4 w-4" />
              </button>
            </DialogTitle>
          </DialogHeader>

          {!tab ? (
            <div className="grid grid-cols-3 gap-2">
              <QuickTile icon={<Banknote className="h-5 w-5" />} label={__t("components.quickentryfab.online_upi")} onClick={() => setTab("online")} testId="quick-pick-online" />
              <QuickTile icon={<CreditCard className="h-5 w-5" />} label={__t("manager.kpi_credit")} onClick={() => setTab("credit")} testId="quick-pick-credit" />
              <QuickTile icon={<Receipt className="h-5 w-5" />} label={__t("components.quickentryfab.expense")} onClick={() => setTab("expense")} testId="quick-pick-expense" />
            </div>
          ) : tab === "online" ? (
            <QuickOnlineForm onDone={() => { setOpen(false); setTab(null); }} />
          ) : tab === "credit" ? (
            <QuickCreditForm onDone={() => { setOpen(false); setTab(null); }} />
          ) : (
            <QuickExpenseForm onDone={() => { setOpen(false); setTab(null); }} />
          )}

          {tab && (
            <DialogFooter>
              <Button variant="ghost" onClick={() => setTab(null)} data-testid="quick-back">← Back</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}


function QuickTile({ icon, label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-2 rounded-lg border-2 border-border bg-card p-4 text-foreground/80 transition hover:border-amber-400 hover:bg-amber-50"
      data-testid={testId}
    >
      <div className="text-amber-600">{icon}</div>
      <div className="text-xs font-semibold">{label}</div>
    </button>
  );
}


function QuickOnlineForm({ onDone }) {
  const { t: __t } = useTranslation();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("UPI");
  const [ref, setRef] = useState("");
  const [methods, setMethods] = useState([]);
  const [pump, setPump] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/reference/online-methods").catch(() => ({ data: { methods: [] } })),
      api.get("/auth/me").catch(() => ({ data: {} })),
    ]).then(([m, me]) => {
      const master = m.data.methods || [];
      const enabled = me.data?.pump?.online_methods || ["UPI"];
      setPump(me.data?.pump || null);
      const merged = master.filter((x) => enabled.includes(x.code));
      setMethods(merged.length ? merged : master);
      if (merged.length && !enabled.includes(method)) setMethod(merged[0].code);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!amount || Number(amount) <= 0) return toast.error(__t("csa.csaportal.enter_a_valid_amount"));
    try {
      await api.post("/hisab/draft/online", { amount: Number(amount), method, reference: ref || null });
      toast.success(__t("csa.csaportal.online_payment_logged"));
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3 py-1">
      <div>
        <Label>{__t("csa.csaportal.amount_rs")}</Label>
        <Input
          inputMode="decimal" type="text" pattern="[0-9]*\.?[0-9]*"
          value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
          data-testid="quick-online-amount" className="h-11" autoFocus
        />
      </div>
      <div>
        <Label>{__t("components.quickentryfab.method")}</Label>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger data-testid="quick-online-method" className="h-11"><SelectValue /></SelectTrigger>
          <SelectContent>
            {methods.map((m) => <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>{__t("components.quickentryfab.reference_txn_id_optional")}</Label>
        <Input value={ref} onChange={(e) => setRef(e.target.value)} data-testid="quick-online-ref" className="h-11" />
      </div>
      <Button onClick={submit} className="h-11 w-full" data-testid="quick-online-save" disabled={!amount}>
        Save {pump?.name ? "to " + pump.name : ""}
      </Button>
    </div>
  );
}


function QuickCreditForm({ onDone }) {
  const { t: __t } = useTranslation();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState("");
  const [vehicle, setVehicle] = useState("");

  const doSearch = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (search.trim()) p.set("q", search.trim());
      const { data } = await api.get(`/credit-customers/dropdown/search${p.toString() ? "?" + p : ""}`);
      setResults(data.results || []);
    } catch { /* ignore */ }
  }, [search]);

  useEffect(() => { if (!selected) { const t = setTimeout(doSearch, 200); return () => clearTimeout(t); } }, [doSearch, selected]);

  const submit = async () => {
    if (!selected) return toast.error(__t("components.quickentryfab.pick_a_credit_customer"));
    if (!amount || Number(amount) <= 0) return toast.error(__t("csa.csaportal.enter_a_valid_amount"));
    try {
      await api.post("/hisab/draft/credit", {
        credit_customer_id: selected.id,
        amount: Number(amount),
        vehicle: vehicle || selected.vehicle || null,
      });
      toast.success(__t("csa.csaportal.credit_entry_saved"));
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3 py-1">
      {!selected ? (
        <>
          <div>
            <Label>{__t("csa.csaportal.search_credit_customer")}</Label>
            <Input
              placeholder={__t("components.quickentryfab.type_name_phone_vehicle")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="quick-credit-search" className="h-11" autoFocus
            />
          </div>
          {results.length > 0 && (
            <div className="max-h-52 divide-y divide-slate-100 overflow-auto rounded-md border border-border bg-card">
              {results.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelected(r)}
                  className="flex w-full items-center justify-between p-2 text-left text-xs hover:bg-background"
                  data-testid={`quick-credit-pick-${r.id}`}
                >
                  <div>
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-muted-foreground">{r.phone || ""}{r.vehicle ? " · " + r.vehicle : ""}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">Rs. {Number(r.outstanding || 0).toLocaleString("en-IN")}</div>
                    {r.credit_limit > 0 && <div className="text-[10px] text-muted-foreground">of Rs. {Number(r.credit_limit).toLocaleString("en-IN")}</div>}
                  </div>
                </button>
              ))}
            </div>
          )}
          {search && results.length === 0 && (
            <p className="text-xs text-muted-foreground">{__t("components.quickentryfab.no_matches_ask_your_dealer_to_add_this_c")}</p>
          )}
        </>
      ) : (
        <>
          <div className="flex items-start justify-between rounded-md bg-amber-50 p-2">
            <div className="min-w-0">
              <div className="text-[10px] uppercase text-amber-800">{__t("components.quickentryfab.selected")}</div>
              <div className="truncate text-sm font-semibold">{selected.name}</div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setSelected(null)} data-testid="quick-credit-change">{__t("csa.csaportal.change")}</Button>
          </div>
          <div>
            <Label>{__t("csa.csaportal.amount_rs")}</Label>
            <Input
              inputMode="decimal" type="text" pattern="[0-9]*\.?[0-9]*"
              value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
              data-testid="quick-credit-amount" className="h-11" autoFocus
            />
          </div>
          <div>
            <Label>{__t("components.quickentryfab.vehicle_optional_override")}</Label>
            <Input value={vehicle} onChange={(e) => setVehicle(e.target.value.toUpperCase())} data-testid="quick-credit-vehicle" className="h-11" />
          </div>
          <Button onClick={submit} className="h-11 w-full" data-testid="quick-credit-save" disabled={!amount}>{__t("components.quickentryfab.save_credit_entry")}</Button>
        </>
      )}
    </div>
  );
}


function QuickExpenseForm({ onDone }) {
  const { t: __t } = useTranslation();
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [category, setCategory] = useState("MISC");
  const [mode, setMode] = useState("CASH");
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    api.get("/expenses/categories").then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, []);
  const submit = async () => {
    if (!amount || Number(amount) <= 0) return toast.error(__t("csa.csaportal.enter_a_valid_amount"));
    if (!desc.trim()) return toast.error(__t("csa.csaportal.description_is_required"));
    try {
      await api.post("/hisab/draft/expense", {
        amount: Number(amount), description: desc.trim(),
        category, payment_mode: mode,
      });
      toast.success(__t("csa.csaportal.expense_added"));
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3 py-1">
      <div>
        <Label>{__t("components.quickentryfab.category")}</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="quick-expense-category" className="h-11"><SelectValue /></SelectTrigger>
          <SelectContent>
            {categories.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>{__t("csa.csaportal.amount_rs")}</Label>
        <Input
          inputMode="decimal" type="text" pattern="[0-9]*\.?[0-9]*"
          value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
          data-testid="quick-expense-amount" className="h-11" autoFocus
        />
      </div>
      <div>
        <Label>{__t("csa.description")}</Label>
        <Input value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="quick-expense-desc" className="h-11" />
      </div>
      <div>
        <Label>{__t("csa.csaportal.payment_mode")}</Label>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger data-testid="quick-expense-mode" className="h-11"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="CASH">{__t("csa.csaportal.cash")}</SelectItem>
            <SelectItem value="UPI">{__t("csa.csaportal.upi")}</SelectItem>
            <SelectItem value="CARD">{__t("csa.csaportal.card")}</SelectItem>
            <SelectItem value="BANK">{__t("csa.csaportal.bank_transfer")}</SelectItem>
            <SelectItem value="CHEQUE">{__t("csa.csaportal.cheque")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button onClick={submit} className="h-11 w-full" data-testid="quick-expense-save" disabled={!amount || !desc}>
        Save expense
      </Button>
    </div>
  );
}
